import React from 'react';
import { DayPicker } from 'react-day-picker';
import { clsx } from 'clsx';
import styles from './Calendar.module.scss';
import { ro } from 'react-day-picker/locale';

export interface CalendarProps {
  /**
   * Selected date
   */
  selected?: Date;

  /**
   * Callback when date is selected
   */
  onSelect?: (date: Date | undefined) => void;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Caption layout for navigation
   */
  captionLayout?: 'dropdown' | 'dropdown-months' | 'dropdown-years' | 'label';

  /**
   * Disabled dates (can be Date[], function, or object with before/after)
   */
  disabled?:
    | Date[]
    | ((date: Date) => boolean)
    | { before?: Date; after?: Date };

  /**
   * Start month for navigation
   */
  startMonth?: Date;

  /**
   * End month for navigation
   */
  endMonth?: Date;

  /**
   * Minimum selectable date (fromDate)
   */
  fromDate?: Date;

  /**
   * Maximum selectable date (toDate)
   */
  toDate?: Date;

  /**
   * Timezone for date handling
   */
  timeZone?: string;

  /**
   * The month to display initially when the calendar is opened
   */
  defaultMonth?: Date;

  /**
   * The month to display (controlled mode). Use this to programmatically control which month is visible.
   */
  month?: Date;

  /**
   * Callback when month changes (for controlled mode)
   */
  onMonthChange?: (month: Date) => void;
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      selected,
      onSelect,
      className,
      captionLayout = 'dropdown',
      disabled,
      startMonth,
      endMonth,
      fromDate,
      toDate,
      timeZone = 'Europe/Bucharest',
      defaultMonth,
      month,
      onMonthChange,
      ...props
    },
    ref
  ) => {
    const calendarClasses = clsx(styles.calendar, className);

    // Combine fromDate/toDate with disabled dates
    const getDisabledDates = ():
      | Date[]
      | ((date: Date) => boolean)
      | { before: Date; after: Date }
      | undefined => {
      if (fromDate || toDate) {
        // If there are additional disabled dates, combine them
        if (disabled) {
          if (Array.isArray(disabled)) {
            // Combine range with array of dates
            return (date: Date) => {
              const isBeforeMin = fromDate && date < fromDate;
              const isAfterMax = toDate && date > toDate;
              const isInDisabledArray = disabled.some(
                (disabledDate) =>
                  date.toDateString() === disabledDate.toDateString()
              );
              return isBeforeMin || isAfterMax || isInDisabledArray;
            };
          } else if (typeof disabled === 'function') {
            // Combine range with function
            return (date: Date) => {
              const isBeforeMin = fromDate && date < fromDate;
              const isAfterMax = toDate && date > toDate;
              return isBeforeMin || isAfterMax || disabled(date);
            };
          } else {
            // Combine range with object - ensure both dates are defined
            const combined: { before: Date; after: Date } = {
              before: disabled.before || fromDate!,
              after: disabled.after || toDate!,
            };
            return combined;
          }
        }
        // Only return range object if both dates are defined
        if (fromDate && toDate) {
          return { before: fromDate, after: toDate };
        }
        // Otherwise use a function for partial ranges
        return (date: Date): boolean => {
          const isBeforeMin = fromDate ? date < fromDate : false;
          const isAfterMax = toDate ? date > toDate : false;
          return isBeforeMin || isAfterMax;
        };
      }
      // Type guard for disabled prop
      if (!disabled) return undefined;
      if (Array.isArray(disabled)) return disabled;
      if (typeof disabled === 'function') return disabled;
      // For object type, ensure both dates are defined or return undefined
      if (disabled.before && disabled.after) {
        return { before: disabled.before, after: disabled.after };
      }
      return undefined;
    };

    return (
      <div ref={ref} className={styles.wrapper} {...props}>
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={onSelect}
          className={calendarClasses}
          captionLayout={captionLayout}
          disabled={getDisabledDates()}
          startMonth={startMonth}
          endMonth={endMonth}
          defaultMonth={defaultMonth}
          month={month}
          onMonthChange={onMonthChange}
          locale={ro}
          timeZone={timeZone}
          ISOWeek
        />
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';
